//#region
import React, { useEffect, useState } from 'react';

// redux
import { useSelector } from 'react-redux';
import { selectBookingsByFlight, selectTaskIds } from '_redux/slices/timeline/timeline-bookings';
import { selectThirtyMinLayout } from "_redux/slices/timeline/timeline-control";
import { selectSelectedTask, selectSelectedTasksList, selectSelectedMultiplePAXTask } from "_redux/slices/timeline/selected-task";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectIata } from '_redux/slices/main-screen/user';

// custom components
import JobCard from '../job-card';

// icons
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

//#endregion

// build a single-passenger boarding booking per passenger of a grouped boarding booking
// duplicate-style _id so confirm() preserves it verbatim and it filters like a client duplicate
const buildPerPaxBoardingBookings = (job) =>
  (job?.passengers || []).map((pax) => ({
    ...job,                       // keep flightConfirmationDetails/flightDetails/type flags
    _id: `${pax._id ?? pax.originalBookingId}-duplicate-boarding`,
    assistance: pax.assistance ?? job.assistance ?? [],
    name: pax.originalBookingDoc?.name ?? pax.name ?? job.name,
    passengers: [pax],            // single-passenger group
    combinedBooking: true,
    isBoardingBooking: true,
    isClientDuplicate: true,
  }));

const JobList = (props) => {

  // component will only re-render when bookings for this flight change
  const bookings = useSelector(selectBookingsByFlight(props?.flightKey));
  const thirtyMinLayout = useSelector(selectThirtyMinLayout);

  // OPTIMIZED: Get these once and pass to all JobCards to reduce Redux subscriptions
  const selectedTask = useSelector(selectSelectedTask);
  const selectedTasksList = useSelector(selectSelectedTasksList);
  const usedTheme = useSelector(selectUsedTheme);
  const selectedMultiplePAXTask = useSelector(selectSelectedMultiplePAXTask);
  const iata = useSelector(selectIata);
  const taskIds = useSelector(selectTaskIds);           // allocated task ids, used to hide allocated per-pax boarding cards

  const [clicked, setClicked] = useState([]);

  // track which grouped boarding bookings are expanded into per-pax cards (PUJ only)
  const [expandedBoardingIds, setExpandedBoardingIds] = useState({});

  const toggleExpanded = (jobId) => {
    setExpandedBoardingIds(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  // Track click order for dynamic z-index
  const [clickOrder, setClickOrder] = useState({});
  const [clickCounter, setClickCounter] = useState(1000); // Start high to override default z-index

  const handleCardClick = (jobId) => {
    setClickOrder(prev => ({
      ...prev,
      [jobId]: clickCounter
    }));
    setClickCounter(prev => prev + 1);
  };

  // if no bookings, return null
  if (!bookings || bookings.length === 0) {
    return null;
  }

  return (
    <div style={{ padding: '4px', margin: '2px 0' }}>

      {/* list of bookings */}
      {bookings.map((job, index) => {

        // shared props for grouped + per-pax cards
        const sharedCardProps = {
          index,
          timelineJobs: true,
          flightData: props.flightData,
          isBoardingFlight: props.isBoardingFlight,
          thirtyMinLayout,
          selectedTask,
          selectedTasksList,
          usedTheme,
          selectedMultiplePAXTask,
          clicked,
          setClicked,
          showFullCard: props?.showFullCard,
        };

        // PUJ only: grouped boarding bookings with more than 1 pax can expand into per-pax cards
        const isExpandableBoarding = iata === 'PUJ' && job?.combinedBooking && job?.isBoardingBooking && (job?.passengers?.length > 1);

        if (!isExpandableBoarding) {
          return (
            <JobCard
              key={job._id || index}
              job={job}
              {...sharedCardProps}
              zIndex={clickOrder[job._id] || index + 1}
              onCardClick={() => handleCardClick(job._id)}
            />
          );
        }

        const isExpanded = !!expandedBoardingIds[job._id];

        // per-pax boarding cards, hiding any pax already allocated as an individual boarding task
        const perPaxBookings = isExpanded
          ? buildPerPaxBoardingBookings(job).filter((paxJob) => !taskIds?.includes(paxJob._id))
          : [];

        return (
          <div key={job._id || index}>

            {/* grouped boarding card (unchanged) */}
            <JobCard
              job={job}
              {...sharedCardProps}
              zIndex={clickOrder[job._id] || index + 1}
              onCardClick={() => handleCardClick(job._id)}
            />

            {/* expand / collapse chevron toggle */}
            <div
              onClick={() => toggleExpanded(job._id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '1px 0',
                fontSize: 10,
                color: '#555',
                userSelect: 'none',
              }}
            >
              {isExpanded ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
              <span>{isExpanded ? 'Hide passengers' : `Show passengers (${job?.passengers?.length})`}</span>
            </div>

            {/* per-pax boarding cards */}
            {isExpanded && (
              <div style={{ marginLeft: '8px' }}>
                {perPaxBookings.map((paxJob) => (
                  <JobCard
                    key={paxJob._id}
                    job={paxJob}
                    {...sharedCardProps}
                    zIndex={clickOrder[paxJob._id] || index + 1}
                    onCardClick={() => handleCardClick(paxJob._id)}
                  />
                ))}
              </div>
            )}

          </div>
        );
      })}

    </div>
  );
};

export default React.memo(JobList, (prevProps, nextProps) => {
  return (
    prevProps.flightKey === nextProps.flightKey &&
    prevProps.flightData === nextProps.flightData &&
    prevProps.isBoardingFlight === nextProps.isBoardingFlight
  );
});

